import { useEffect, useRef, useState, useCallback } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useWebSocket } from '../../providers/WebSocketProvider';
import { useNavigate } from 'react-router';
import { formatPhone } from '../../../lib/phone-format';
import { useAssignedInboundFilter } from '../../hooks/useAssignedInboundFilter';

interface InboundNotification {
  id: string;
  contactId: string;
  from?: string;
  preview: string;
  channel: string;
  timestamp: number;
}

export function InboundToast() {
  const { subscribe } = useWebSocket();
  const { shouldNotify } = useAssignedInboundFilter();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<InboundNotification[]>([]);
  const isOnCallRef = useRef(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { phase?: string } | null;
      isOnCallRef.current = !!(detail && detail.phase !== 'ended');
    };
    window.addEventListener('apex:call-update', handler);
    return () => window.removeEventListener('apex:call-update', handler);
  }, []);

  // Listen for inbound messages
  useEffect(() => {
    return subscribe('msg.received', (raw: unknown) => {
      const event = raw as {
        data?: {
          contact_id?: string;
          contact_name?: string;
          from_number?: string;
          body?: string;
          message_id?: string;
          assigned_rep_id?: string | null;
        };
      };

      // Assigned-contact scoping (#1471) — mirrors the NotificationBell filter
      // so the toast, chime, browser notification, and title flash all skip
      // messages the user shouldn't be notified about.
      if (!shouldNotify(event.data?.assigned_rep_id)) return;

      // Prefer the contact's name (enriched server-side, #1344); fall back to a
      // formatted phone, then 'Unknown', so the header is never a raw E.164.
      const fromNumber = event.data?.from_number;
      const displayFrom =
        event.data?.contact_name || (fromNumber ? formatPhone(fromNumber) : '') || 'Unknown';

      const notification: InboundNotification = {
        id: event.data?.message_id || String(Date.now()),
        contactId: event.data?.contact_id || '',
        from: displayFrom,
        preview: event.data?.body || 'New message',
        channel: 'sms',
        timestamp: Date.now(),
      };

      setNotifications((prev) => [notification, ...prev].slice(0, 3));

      // Auto-dismiss after 8 seconds
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      }, 8000);

      // Play audio chime via Web Audio API (no external file needed)
      if (!isOnCallRef.current) {
        try {
          const ctx = new AudioContext();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
          gain.gain.setValueAtTime(0.15, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        } catch {
          // Web Audio not available
        }
      }

      // Browser push notification (if permission granted)
      if (Notification.permission === 'granted') {
        try {
          new Notification(`New message from ${notification.from}`, {
            body: notification.preview,
            icon: '/brand/apex-favicon.svg',
            tag: notification.id,
          });
        } catch {
          // Notification API not available
        }
      }

      // Tab title flash
      if (document.hidden) {
        const originalTitle = document.title;
        let flashing = true;
        const flashInterval = setInterval(() => {
          document.title = flashing ? `(${notification.from}) New Message` : originalTitle;
          flashing = !flashing;
        }, 1000);

        const stopFlash = () => {
          clearInterval(flashInterval);
          document.title = originalTitle;
          document.removeEventListener('visibilitychange', stopFlash);
        };
        document.addEventListener('visibilitychange', stopFlash);

        // Stop after 30 seconds regardless
        setTimeout(stopFlash, 30000);
      }
    });
  }, [subscribe, shouldNotify]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const handleClick = useCallback(
    (notification: InboundNotification) => {
      dismiss(notification.id);
      navigate('/inbox');
    },
    [dismiss, navigate],
  );

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
      <AnimatePresence>
        {notifications.map((n) => (
          <motion.div
            key={n.id}
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="pointer-events-auto bg-card border border-border/60 border-l-4 border-l-primary rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-4 max-w-[360px] cursor-pointer flex gap-3"
            onClick={() => handleClick(n)}
          >
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-4 h-4 text-primary stroke-[1.5]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-[0.8125rem] font-semibold text-foreground truncate">
                  {n.from}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss(n.id);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[0.75rem] text-muted-foreground truncate">{n.preview}</p>
              <span className="text-[0.625rem] text-muted-foreground/60 uppercase font-medium mt-1 inline-block">
                {n.channel}
              </span>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
